import { useCallback, useMemo, useState } from "react";

import { Dialog } from "@/design-system/components/Dialog/Dialog";
import { EditProfileForm } from "@/account/components/EditProfileForm/EditProfileForm";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import useUsers from "@/hooks/useUsers";
import {
  hasSeenProfileNamePrompt,
  markProfileNamePromptSeen,
} from "./profileNamePrompt";

export function RequiredProfileNameModal() {
  const users = useUsers();
  const accountMetadata = useAccountMetadata();
  const [dismissed, setDismissed] = useState(false);

  const open = useMemo(() => {
    if (dismissed || users.loading || !users.currentUser) return false;

    // RequiredAccountInfoCollectionModal takes priority: don't stack a
    // skippable name prompt on top of a hard-gated modal for new admins.
    const accountInfoModalOpen =
      accountMetadata.loaded &&
      users.currentUser.role === "admin" &&
      !accountMetadata.companyDetails?.name;
    if (accountInfoModalOpen) return false;

    // Whether the user has a name is deliberately not part of this: the
    // prompt is a one-time introduction to the profile, shown once and then
    // retired by saving or dismissing it. Someone who already has a name can
    // still add a title or a photo, or just close it.
    return !hasSeenProfileNamePrompt(users.currentUser.uid);
  }, [dismissed, users.currentUser, users.loading, accountMetadata]);

  const retire = useCallback(() => {
    if (users.currentUser) {
      markProfileNamePromptSeen(users.currentUser.uid);
    }
    setDismissed(true);
  }, [users.currentUser]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) retire();
      }}
      title="Tell us more about you"
    >
      <p className="text-textSubtle mb-4 text-sm">
        These details help teammates know who's who. You can always fill them in
        later from your profile menu.
      </p>
      <EditProfileForm onSaved={retire} />
    </Dialog>
  );
}
